"use client";

import { useState } from "react";
import Lottie from "lottie-react";
import { Typography } from "./typography";
import runningSquirrel from "./running-squirrel.json";

const PHRASES = [
  "Going nuts",
  "Scurrying",
  "Cracking on",
  "Foraging",
  "Stashing nuts",
  "Digging in",
  "Nut hunting",
  "Squirrelling away",
  "Cracking shells",
  "Hoarding goodies",
];

type Props = {
  label?: string;
};

/**
 * Animated thinking indicator shown while Nutcake is working — a running
 * squirrel cycling through foraging phrases via the `phrase-cycle` CSS
 * animation in the shared globals. The phrase order is shuffled once per
 * mount (not per render) so parent re-renders don't restart the cycle.
 *
 * With a `label`, the cycling is replaced by a single static line combining
 * one random phrase with the given context, e.g.
 * "Squirrelling away · Getting #AD posts for @looseunicorns".
 */
export const SquirrelTypingIndicator = ({ label }: Props) => {
  const [phrases] = useState(() =>
    [...PHRASES].sort(() => Math.random() - 0.5),
  );

  return (
    <div className="flex items-center gap-1">
      <div className="h-4 w-5 shrink-0" aria-hidden="true">
        <Lottie
          animationData={runningSquirrel}
          loop
          autoplay
          style={{ width: "100%", height: "100%" }}
        />
      </div>
      {label ? (
        <Typography size="sm" color="neutral">
          {phrases[0]} · {label}
        </Typography>
      ) : (
        <div className="relative flex-1">
          {phrases.map((phrase, i) => (
            <Typography
              size="sm"
              key={phrase}
              className="phrase-cycle text-primary-700 absolute top-1/2 -translate-y-1/2 text-sm whitespace-nowrap"
              data-index={i}
            >
              {phrase}…
            </Typography>
          ))}
        </div>
      )}
    </div>
  );
};
